import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { format, addDays, addMonths } from "date-fns";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

interface ValidationResponse {
  valid: boolean;
  message: string;
  conflicts: string[];
}

interface StartDateStepProps {
  employeeId: number;
  value: string | null;
  onChange: (value: string | null) => void;
  onValidation: (isValid: boolean) => void;
}

export const StartDateStep: React.FC<StartDateStepProps> = ({
  employeeId,
  value,
  onChange,
  onValidation,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(`${value}T12:00:00`) : undefined,
  );
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message: string;
    conflicts: string[];
  } | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const today = new Date();
  const maxDate = addMonths(today, 6); // 6 months in the future

  useEffect(() => {
    if (selectedDate) {
      validateStartDate(selectedDate);
    } else {
      setValidationResult(null);
      onValidation(false);
    }
  }, [selectedDate]);

  const validateStartDate = async (date: Date) => {
    setIsValidating(true);
    try {
      const response = await ApiService.request<ValidationResponse>(
        EndpointKeys.VALIDATE_SETUP_STEP,
        {
          method: "POST",
          urlParams: { employeeId },
          body: {
            step: "start_date",
            start_date: format(date, "yyyy-MM-dd"),
          },
        },
      );

      const result = {
        valid: response.valid,
        message: response.message,
        conflicts: response.conflicts || [],
      };

      setValidationResult(result);
      onValidation(result.valid);
    } catch (error) {
      console.error("Validation error:", error);
      setValidationResult({
        valid: false,
        message: "Failed to validate start date",
        conflicts: ["Validation service unavailable"],
      });
      onValidation(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    // Fix timezone issue by using the local date parts instead of ISO conversion
    // This ensures the selected date is preserved exactly as displayed
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    // Format as YYYY-MM-DD without timezone conversion
    const formattedDate = `${year}-${month}-${day}`;

    setSelectedDate(date);
    onChange(formattedDate);
  };

  const formatDisplayDate = (date: Date) => {
    return format(date, "dd/MM/yyyy"); // UK date format
  };

  const getQuickDateOptions = () => {
    return [
      { label: "Today", date: today },
      { label: "Tomorrow", date: addDays(today, 1) },
      { label: "Next Monday", date: getNextMonday() },
      { label: "Start of next month", date: getStartOfNextMonth() },
    ];
  };

  const getNextMonday = () => {
    const date = new Date(today);
    const day = date.getDay();
    const daysUntilMonday = day === 0 ? 1 : 8 - day;
    return addDays(date, daysUntilMonday);
  };

  const getStartOfNextMonth = () => {
    const date = new Date(today);
    date.setMonth(date.getMonth() + 1, 1);
    return date;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || date > maxDate;
  };

  return (
    <div className="space-y-6 px-0">
      {/* Quick Date Options */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Quick options:</p>
        <div className="flex flex-wrap gap-2">
          {getQuickDateOptions().map((option) => (
            <Button
              key={option.label}
              variant="outline"
              size="sm"
              onClick={() => {
                if (!isDateDisabled(option.date)) {
                  handleDateSelect(option.date);
                }
              }}
              disabled={isDateDisabled(option.date)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Manual Date Input */}
      <div className="space-y-2 p-1">
        <Label htmlFor="manual-date">Or enter date manually</Label>
        <Input
          id="manual-date"
          type="date"
          value={value || ""}
          onChange={(e) => {
            const dateValue = e.target.value;
            if (dateValue) {
              const date = new Date(`${dateValue}T12:00:00`);
              if (!isDateDisabled(date)) {
                setSelectedDate(date);
                onChange(dateValue);
              }
            } else {
              setSelectedDate(undefined);
              onChange(null);
            }
          }}
          min={format(today, "yyyy-MM-dd")}
          max={format(maxDate, "yyyy-MM-dd")}
        />
      </div>

      {/* Validation Status */}
      {isValidating && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Validating start date...</AlertDescription>
        </Alert>
      )}

      {validationResult && !isValidating && (
        <Alert
          className={
            validationResult.valid
              ? "border-green-200 bg-green-50"
              : "border-red-200 bg-red-50"
          }
        >
          {validationResult.valid ? (
            <CheckCircle className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-600" />
          )}
          <AlertDescription
            className={
              validationResult.valid ? "text-green-800" : "text-red-800"
            }
          >
            <div>
              <p className="font-medium">{validationResult.message}</p>
              {validationResult.conflicts.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-sm">
                  {validationResult.conflicts.map((conflict, index) => (
                    <li key={index}>{conflict}</li>
                  ))}
                </ul>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Helpful Information */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mx-0">
        <h4 className="font-medium text-blue-900 mb-2">Important Notes:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Start date can be today or up to 6 months in the future</li>
          <li>• This date cannot be changed after completing the setup</li>
        </ul>
      </div>
    </div>
  );
};
